/** @format */

import {
	AthleteMfaDeviceResponse,
	AthleteProfileResponse,
	ChangeEmail,
	ValidateChangeEmail,
	VerificationCodeRequest,
	VerificationRequest
} from '@my-account/data-access/interfaces';

import { HttpHeaders } from '@angular/common/http';
import { HttpService } from '@shared/data-access/services';
import { Injectable } from '@angular/core';
import { MediaType } from '@shared/data-access/enums';
import { Observable } from 'rxjs';

/* istanbul ignore file */

@Injectable({
	providedIn: 'root'
})
export class AthleteEngineDao {
	private url = `/api/v1/athletes/emails`;
	private emailUrl = `/api/v1/athletes/emails`;
	private profileUrl = `/api/v1/athletes/profile`;
	private verificationUrl = `/api/v1/athletes/verification`;
	private mfaUrl = `/api/v1/athletes/mfa`;

	constructor(private http: HttpService) {}

	// Default HTTP Header properties
	private DEFAULT_HTTP_HEADER_PROPERTIES = {
		Accept: MediaType.APPLICATION_JSON,
		'Content-Type': MediaType.APPLICATION_JSON,
		'Access-Control-Allow-Origin': '*',
		'Cache-Control': 'no-cache,no-store,must-revalidate,max-age=0',
		Pragma: 'no-cache',
		Expires: 'Sat, 01 Jan 2000 00:00:00 GMT'
	};

	// Default HTTP Headers object
	private DEFAULT_HTTP_HEADERS = new HttpHeaders(this.DEFAULT_HTTP_HEADER_PROPERTIES);

	/**
	 * @description
	 * Send reset link to mail
	 */
	public sendResetLink(): Observable<any> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true,
			useApiAuth: true
		};

		return this.http.post(this.url, undefined, httpOptions);
	}

	/**
	 * @description
	 * Updates the athlete's email address
	 */
	public updateEmail(changeEmail: ChangeEmail): Observable<any> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true,
			useApiAuth: true
		};

		return this.http.put(this.url, changeEmail, httpOptions);
	}

	/**
	 * @description
	 * Updates the athlete's email address
	 */
	public validateTicketId(validateChangeEmail: ValidateChangeEmail): Observable<any> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true,
			useApiAuth: true
		};

		return this.http.patch(this.url, validateChangeEmail, httpOptions);
	}

	/**
	 * @description
	 * Retrieves athlete's profile info from Auth0
	 */
	public retrieveAthleteProfile(): Observable<AthleteProfileResponse> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADER_PROPERTIES,
			withCredentials: true,
			useApiAuth: true
		};
		return this.http.get(this.profileUrl, null, httpOptions);
	}

	/**
	 * @description
	 * Triggers the verification flow for either email verification or MFA device removal
	 */
	public triggerVerificationFlow(action: VerificationRequest): Observable<any> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADER_PROPERTIES,
			withCredentials: true,
			useApiAuth: true
		};
		return this.http.post(this.verificationUrl, action, httpOptions);
	}

	/**
	 * @description
	 * Validates an athlete's email verification code and updates their email verification status
	 */
	public validateEmailVerificationCode(
		verificationCodeRequest: VerificationCodeRequest
	): Observable<any> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADER_PROPERTIES,
			withCredentials: true,
			useApiAuth: true
		};
		// eslint-disable-next-line prefer-template
		return this.http.patch(
			this.emailUrl + '/verification',
			verificationCodeRequest,
			httpOptions
		);
	}

	/**
	 * @description
	 * Enables MFA login flow for an athlete
	 */
	public enableMFA(): Observable<any> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADER_PROPERTIES,
			withCredentials: true,
			useApiAuth: true
		};
		return this.http.put(this.mfaUrl, null, httpOptions);
	}

	/**
	 * @description
	 * Retrieves the athlete's registered MFA devices
	 */
	public retrieveMFADevices(): Observable<AthleteMfaDeviceResponse[]> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADER_PROPERTIES,
			withCredentials: true,
			useApiAuth: true
		};
		// eslint-disable-next-line prefer-template
		return this.http.get(this.mfaUrl + '/devices', null, httpOptions);
	}

	/**
	 * @description
	 * Triggers the MFA enrollment flow for an athlete to add SMS as an MFA device
	 */
	public triggerMFAEnrollmentFlow(): Observable<any> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADER_PROPERTIES,
			withCredentials: true,
			useApiAuth: true
		};
		// eslint-disable-next-line prefer-template
		return this.http.post(this.mfaUrl + '/devices/enrollment', null, httpOptions);
	}

	/**
	 * @description
	 * Deletes the athlete's registered MFA authenticator
	 */
	public deleteMFAAuthenticator(authenticatorId: string): Observable<any> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADER_PROPERTIES,
			withCredentials: true,
			useApiAuth: true
		};
		// eslint-disable-next-line prefer-template
		return this.http.delete(this.mfaUrl + '/devices' + '/' + authenticatorId, httpOptions);
	}

	/**
	 * @description
	 * Disables MFA login flow for an athlete
	 */
	public disableMFA(code: string): Observable<any> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADER_PROPERTIES,
			withCredentials: true,
			useApiAuth: true
		};
		// eslint-disable-next-line prefer-template
		return this.http.delete(this.mfaUrl + '?code=' + code, httpOptions);
	}
}
